import * as React from "react";
import Modal from "../../misc/components/modal";
import Alert, { AlertCategoryEnum } from "../../misc/components/Alert";
import { parse } from "csv/browser/esm";
import { useWebPortalApiClient } from "../../misc/api/WebPortalTokenClient";
import { DeviceCreation, DeviceMutation } from "webportalclient";
import { validate as isGuid } from 'uuid';

interface ImportDevicesProps {
    onClose: () => void;
    setImportSucceeded: React.Dispatch<React.SetStateAction<boolean>>;
}

export const ImportDevices = ({ onClose, setImportSucceeded }: ImportDevicesProps) => {
    const [selectedFile, setSelectedFile] = React.useState<File>();
    const [error, setError] = React.useState<string | undefined>();
    const client = useWebPortalApiClient();
    const requiredMessage = "Please select a CSV file";

    const onFileChange = (event) => {
        const file = event.target.files[0];
        if (!file || file.type !== 'text/csv') {
            setError(requiredMessage);
            setSelectedFile(null);
            event.target.value = null;
            return;
        }
        setSelectedFile(file);
        setError(null);
    }

    const onSaveClick = async () => {
        setError(undefined);
        const reader = new FileReader();

        reader.onload = async (event) => {
            const csvContent = event.target.result as string;
            const normalizedCSV = csvContent.replace(/\r\n/g, '\n').replace(/\r/g, '\n');

            parse(normalizedCSV, {
                delimiter: ",",
                skip_empty_lines: true,
                columns: ["Study Guid", "Device Type", "Serial Number", "Allocated Site Guid", "Allocated Subject Guid"],
            }, async (err, data) => {
                if (err) {
                    setError(err.message);
                    return;
                }

                try {
                    for (let i = 0; i < data.length; i++) {
                        const record = data[i];
                        
                        const studyId = record["Study Guid"];
                        const deviceType = record["Device Type"];
                        const serialNumber = record["Serial Number"];
                        const allocatedSiteId = record["Allocated Site Guid"];
                        const allocatedSubjectId = record["Allocated Subject Guid"];
                        
                        if (i === 0) {
                            let invalidHeaders = [];
                            
                            if (studyId && studyId !== 'Study Guid') {
                                invalidHeaders.push('Study Guid');
                            }
                            if (!deviceType || deviceType !== 'Device Type') {
                                invalidHeaders.push('Device Type');
                            }
                            if (!serialNumber || serialNumber !== 'Serial Number') {
                                invalidHeaders.push('Serial Number');
                            }
                            if (allocatedSiteId && allocatedSiteId !== 'Allocated Site Guid') {
                                invalidHeaders.push('Allocated Site Guid');
                            }
                            if (allocatedSubjectId && allocatedSubjectId !== 'Allocated Subject Guid') {
                                invalidHeaders.push('Allocated Subject Guid');
                            }

                            if (invalidHeaders.length > 0) {
                                setError(`Invalid header: couldn't find ${invalidHeaders.map(header => `'${header}'`).join(', ')}.`);
                                return;
                            }

                            continue;
                        }

                        const errorMessage = validateGuids(studyId, allocatedSiteId, allocatedSubjectId);
                        if (errorMessage) {
                            setError(errorMessage);
                            setImportSucceeded(false);
                            return;
                        }

                        const device = await client.client.getDevices(undefined, `serialNumber eq '${serialNumber}' and studyId eq ${studyId}`);
                        if (device["@odata.count"] > 0) {
                            continue;
                        }

                        const deviceCreation: DeviceCreation = {
                            studyId: studyId ? studyId : null,
                            deviceType: deviceType,
                            serialNumber: serialNumber,
                            allocatedSiteId: allocatedSiteId ? allocatedSiteId : null,
                            allocatedSubjectId: allocatedSubjectId ? allocatedSubjectId : null,
                        };

                        await client.client.postDevice(deviceCreation);
                    }
                    onClose();
                    setImportSucceeded(true);
                } catch (ex) {
                    setError(ex.message);
                    setImportSucceeded(false);
                }
            });
        };

        reader.readAsText(selectedFile);
    }

    const validateGuids = (studyId, allocatedSiteId, allocatedSubjectId) => {
        let guidProperty = "";
        let guidValue = "";
        let errorMessage = "";

        if (studyId && !isGuid(studyId)) {
            guidProperty = "Study";
            guidValue = studyId;
        } else if (allocatedSiteId && !isGuid(allocatedSiteId)) {
            guidProperty = "Site";
            guidValue = allocatedSiteId;
        } else if (allocatedSubjectId && !isGuid(allocatedSubjectId)) {
            guidProperty = "Subject";
            guidValue = allocatedSubjectId;
        }

        if (guidProperty && guidValue) {
            errorMessage = `'${guidValue}' is not a valid ID for ${guidProperty}`;
        }

        return errorMessage;
    }

    return (
        <Modal show={true} header="Import Devices from CSV" closeModalHandler={onClose}>
            <div className="row">
                <div className="col-md-12">
                    <div className="panel-body">
                        {error ? <Alert show={true} category={AlertCategoryEnum.Danger}>{error}</Alert> : null}
                        <div className="form-group col-md-12">
                            <label htmlFor="sitesFile">CSV File</label>
                            <input id="sitesFile" type="file" accept=".csv" className="form-control" onChange={onFileChange} />
                        </div>
                    </div>
                </div>
            </div>
            <div className="modal-footer">
                <button id="importButton" className="btn btn-primary" type="submit" disabled={!selectedFile} onClick={onSaveClick}>
                    Import
                </button>&nbsp;
                <button id="returnToListButton" className="btn btn-primary btn-outline" onClick={onClose}>
                    Return to List
                </button>
            </div>
        </Modal>
    );
}
